import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './../styles/TokenPurchase.css';

const apiUrl = 'https://api.omegavoid.com';

const TokenPurchase = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedUnlimitedOffer, setSelectedUnlimitedOffer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  
  // For success page
  const [paymentId, setPaymentId] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecoveryInfo, setShowRecoveryInfo] = useState(false);
  const [privateSuiteInfo, setPrivateSuiteInfo] = useState(null);
  const [copied, setCopied] = useState({ paymentId: false, recoveryCode: false });
  
  // Prevent duplicate API calls
  const processedSessionRef = useRef(new Set());
  const isProcessingRef = useRef(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const TOKEN_PACKAGES = [
//    { tokens: 5, price: 1.99, priceId: 'price_1RQWGIDE3j8gVBX0uogTMUb4', isPremium: false },
//    { tokens: 10, price: 4.99, priceId: 'price_1RQWhgDE3j8gVBX0wXre1YOn', isPremium: false },
    { tokens: 50, price: 9.99, priceId: 'price_1RQXytDE3j8gVBX0D0Y2Q6gJ', isPremium: false },
//    { tokens: 100, price: 14.99, priceId: 'price_1RQY5mDE3j8gVBX0xUyCJcoe', isPremium: false },
//    { tokens: 1000, price: 19.99, priceId: 'price_1RQYH7DE3j8gVBX05RXMFYH2', isPremium: false },
    { tokens: 2500, price: 24.99, priceId: 'price_1RQYKsDE3j8gVBX0tLpjlVZ1', isPremium: false },
//    { tokens: 5000, price: 49.99, priceId: 'price_1RQYMaDE3j8gVBX0ewK9s1JX', isPremium: false },
//    { tokens: 15000, price: 119.99, priceId: 'price_1RQYOnDE3j8gVBX09xBzOi7J', isPremium: false },
    { tokens: 37500, price: 199.99, priceId: 'price_1RQYUGDE3j8gVBX0laVHgLIE', isPremium: true },
//    { tokens: 50000, price: 499.99, priceId: 'price_1RQYX7DE3j8gVBX0gxXWs6Mz', isPremium: true },
//    { tokens: 100000, price: 999.99, priceId: 'price_1RQYYjDE3j8gVBX0cSV8nVy4', isPremium: true }
  ];

  // Special unlimited offer
  const UNLIMITED_OFFER = {
    tokens: '10000000',
    price: 201.99,
    priceId: 'price_1RbBb9DE3j8gVBX0aGZTSG2K',
    isPremium: true,
    isUnlimited: true,
    duration: 'Forever',
    maxPurchasers: 5000
  };
  
  // Check for success parameters on page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId && !processedSessionRef.current.has(sessionId) && !isProcessingRef.current) {
      console.log('Processing session:', sessionId);
      fetchPaymentDetails(sessionId);
    }
  }, [location.search]);
  
  const fetchPaymentDetails = async (sessionId) => {
    // Prevent duplicate calls
    if (processedSessionRef.current.has(sessionId) || isProcessingRef.current) {
      console.log('Session already processed or currently processing:', sessionId);
      return;
    }
    
    isProcessingRef.current = true;
    processedSessionRef.current.add(sessionId);
    
    setIsLoading(true);
    try {
      console.log('Making API call for session:', sessionId);
      const response = await fetch(`${apiUrl}/stripe/checkout-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      
      if (response.ok && data.payment_id) {
        setPaymentId(data.payment_id);
        
        // Only set recovery code if it exists (for new payments)
        if (data.recovery_code) {
          setRecoveryCode(data.recovery_code);
        }
        
        if (data.private_suite_id && data.grid_space) {
          setPrivateSuiteInfo({
            privateSuiteId: data.private_suite_id,
            gridSpace: data.grid_space
          });
        }
        
        setShowRecoveryInfo(true);
      } else {
        throw new Error(data.error || 'Failed to complete purchase process');
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setError('Failed to retrieve your purchase details. Please contact support.');
      // Remove from processed set on error so user can retry
      processedSessionRef.current.delete(sessionId);
    } finally {
      setIsLoading(false);
      isProcessingRef.current = false;
    }
  };
  
  const handlePackageSelect = (e) => {
    const packageIndex = parseInt(e.target.value);
    setSelectedPackage(TOKEN_PACKAGES[packageIndex]);
    setSelectedUnlimitedOffer(false); // Clear unlimited selection
  };

  const handleUnlimitedOfferSelect = () => {
    setSelectedUnlimitedOffer(true);
    setSelectedPackage(null); // Clear regular package selection
  };

  const handleEulaChange = (e) => {
    setEulaAccepted(e.target.checked);
  };
  
  const handleCheckout = async () => {
    if (!eulaAccepted) {
      setError('You must agree to the End User License Agreement before proceeding.');
      return;
    }

    const packageToProcess = selectedUnlimitedOffer ? UNLIMITED_OFFER : selectedPackage;
    if (!packageToProcess) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiUrl}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          priceId: packageToProcess.priceId,
          tokens: packageToProcess.tokens.toString(),
          isPremium: packageToProcess.isPremium,
          isUnlimited: packageToProcess.isUnlimited || false,
          eulaAccepted: true,
          eulaAcceptedDate: new Date().toISOString(),
          eulaVersion: 'v1.0 - July 2025'
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to initialize checkout. Please try again.');
      setIsLoading(false);
    }
  };
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({ ...copied, [type]: true });
      setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    });
  };
  
  const continueToLogin = () => {
    navigate('/login');
  };

  // Check if checkout should be enabled
  const isCheckoutEnabled = (selectedPackage || selectedUnlimitedOffer) && eulaAccepted && !isLoading;

  // Show loading state
  if (isLoading) {
    return (
      <div className="token-purchase-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Processing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="token-purchase-container">
      <h1 className="purchase-title">Purchase Tokens</h1>
      
      {!showRecoveryInfo ? (
        <>
          <div className="simple-purchase-layout">
            <div className="token-selector-container">
              <label htmlFor="token-select">Select Token Package:</label>
              <div className="select-wrapper">
                <select 
                  id="token-select" 
                  onChange={handlePackageSelect} 
                  className="token-select"
                  value={selectedPackage ? TOKEN_PACKAGES.indexOf(selectedPackage) : ""}
                >
                  <option value="" disabled>-- Select Tokens --</option>
                  {TOKEN_PACKAGES.map((pkg, index) => (
                    <option key={index} value={index}>
                      {pkg.tokens.toLocaleString()} Tokens - ${pkg.price.toFixed(2)}
                      {pkg.isPremium ? " - Global & Personal Void. Your own separate personal space!" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Special Unlimited Offer Section */}
            <div className="unlimited-offer-container">
              <div className="founders-offer-header">
                <span className="rocket-icon">🚀</span>
                <h2>Founders-Only Offer</h2>
                <span className="limited-badge">Limited Time</span>
              </div>
              
              <div 
                className={`unlimited-offer-card ${selectedUnlimitedOffer ? 'selected' : ''}`}
                onClick={handleUnlimitedOfferSelect}
              >
                <div className="unlimited-offer-content">
                  <h3>10 million Tokens</h3>
                  <div className="unlimited-price">$201.99 USD</div>
                  <div className="unlimited-details">
                    <p>✨ 10 million tokens</p>
                    <p>🔐 Private Void included</p>
                    <p>⚡ Priority support</p>
                    <p className="availability">Available to the first 5,000 users only</p>
                  </div>
                </div>
                <div className="unlimited-select-indicator">
                  {selectedUnlimitedOffer ? '✓ Selected' : 'Select'}
                </div>
              </div>
            </div>
            
            {(selectedPackage || selectedUnlimitedOffer) && (
              <div className="payment-section">
                <div className="selected-package-summary">
                  <h3>Selected Package:</h3>
                  {selectedUnlimitedOffer ? (
                    <p className="selected-package-details">
                      10 million Tokens  - $201.99
                      <span className="premium-note">🚀 Founders-Only Offer - 10 million Tokens + Private Void!</span>
                    </p>
                  ) : (
                    <p className="selected-package-details">
                      {selectedPackage.tokens.toLocaleString()} Tokens - ${selectedPackage.price.toFixed(2)}
                      {selectedPackage.isPremium && 
                        <span className="premium-note">Private Void. Your own separate personal space!</span>
                      }
                    </p>
                  )}
                </div>

                {/* EULA Consent Section */}
                <div className="eula-consent-container">
                  <div className="eula-consent-checkbox">
                    <input 
                      type="checkbox" 
                      id="eulaConsent" 
                      checked={eulaAccepted}
                      onChange={handleEulaChange}
                      required 
                    />
                   <label htmlFor="eulaConsent">
                      I agree to the{' '}
                      <a 
                        href="/documents/eula.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="eula-link"
                      >
                        End User License Agreement (EULA)
                      </a>
                    </label>
                  </div>
                  <div className="eula-footer-note">
                    By proceeding, you acknowledge that you have read and agreed to the EULA above.
                  </div>
                </div>
                
                <button
                  onClick={handleCheckout}
                  disabled={!isCheckoutEnabled}
                  className={`payment-button ${!isCheckoutEnabled ? 'disabled' : ''}`}
                >
                  {isLoading ? 'Processing...' : `Proceed to Checkout`}
                </button>
                
                {error && <div className="payment-error">{error}</div>}
                
                <div className="warning-box">
                  <h3 className="warning-title">⚠️ Important Note</h3>
                  <p>
                    When purchasing tokens, you'll receive both a Login ID and a Recovery Code. 
                    Your Recovery Code will be shown only ONCE during purchase. 
                    Be sure to save it in a secure location.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="youtube-link">
            <a href="https://www.youtube.com/channel/UCTk-XVJUoqISD6jOUvALO5w" target="_blank" rel="noopener noreferrer">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#FF0000">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
            </svg>
              Follow Our YouTube Channel
            </a>
          </div>
          
          <div className="button-container">
            <Link to="/login" className="return-button">
              Return to Login
            </Link>
          </div>
        </>
      ) : (
        <div className="recovery-info-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Purchase Successful!</h2>
            <p>Your tokens have been added to your account. Save the following information securely.</p>
          </div>
          
          <div className="credentials-container">
            <div className="credential-box">
              <h3>Login ID</h3>
              <p>Use this to log in to your account.</p>
              <div className="credential-value">
                {paymentId}
                <button 
                  className="copy-button" 
                  onClick={() => copyToClipboard(paymentId, 'paymentId')}
                >
                  {copied.paymentId ? 'Copied! ✓' : 'Copy'}
                </button>
              </div>
            </div>
            
            {recoveryCode && (
              <div className="credential-box">
                <h3>Recovery Code</h3>
                <p><strong>IMPORTANT:</strong> This is your only way to recover your account if you lose your Login ID.</p>
                <div className="credential-value">
                  {recoveryCode}
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard(recoveryCode, 'recoveryCode')}
                  >
                    {copied.recoveryCode ? 'Copied! ✓' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {privateSuiteInfo && (
            <div className="premium-info-container">
              <h3>Private Void Information</h3>
              <p>You now have access to your own separate personal space!</p>
              <p>Private Void ID: <strong>{privateSuiteInfo.privateSuiteId}</strong></p>
            </div>
          )}
          
          <div className="warning-box">
            <h3 className="warning-title">⚠️ Save this information now!</h3>
            <p>We do not store your credentials in a recoverable format. If you lose both your Login ID and Recovery Code, your account cannot be recovered.</p>
          </div>
          
          <button 
            className="login-redirect-button"
            onClick={continueToLogin}
          >
            Continue to Login
          </button>
          
          <div className="youtube-link">
            <a href="https://www.youtube.com/channel/UCTk-XVJUoqISD6jOUvALO5w" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#FF0000">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62-4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
              Follow Our YouTube Channel
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenPurchase;